import { create } from 'zustand';
import type { Restaurant } from '@/types';
import type { RestaurantFormDataWithLogo } from '@/lib/validations/restaurant';
import { 
  createRestaurant as createRestaurantInFirestore, 
  getRestaurantsByOwner, 
  updateRestaurant as updateRestaurantInFirestore, 
  deleteRestaurant as deleteRestaurantInFirestore 
} from '@/lib/firebase/firestore';
import { uploadRestaurantLogo, validateImageFile } from '@/lib/firebase/storage';
import { deleteRestaurantData } from '@/lib/firebase/cleanup';
import { generateSlug } from '@/lib/utils';

interface RestaurantStore {
  // State
  restaurants: Restaurant[];
  loading: boolean;
  error: string | null;
  uploadProgress: number;
  isUploading: boolean;
  
  // Actions
  fetchRestaurants: (userId: string) => Promise<void>;
  createRestaurant: (userId: string, data: RestaurantFormDataWithLogo) => Promise<Restaurant>;
  updateRestaurant: (id: string, data: Partial<RestaurantFormDataWithLogo>) => Promise<void>;
  deleteRestaurant: (id: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  reset: () => void;
  // New method to check restaurant limit
  checkRestaurantLimit: (userId: string) => Promise<boolean>;
}

export const useRestaurantStore = create<RestaurantStore>((set, get) => ({
  // Initial state
  restaurants: [],
  loading: false,
  error: null,
  uploadProgress: 0,
  isUploading: false,
  
  // Check if user has reached restaurant limit (1 restaurant per user)
  checkRestaurantLimit: async (userId: string) => {
    try {
      const restaurants = await getRestaurantsByOwner(userId);
      return restaurants.length >= 1; // Return true if limit reached
    } catch (error) {
      console.error('Error checking restaurant limit:', error);
      return false; // Allow creation if check fails
    }
  },
  
  // Fetch user restaurants
  fetchRestaurants: async (userId: string) => {
    set({ loading: true, error: null });
    
    try {
      const restaurants = await getRestaurantsByOwner(userId);
      
      // Successfully fetched restaurants (even if empty array)
      set({ 
        restaurants,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      // Only set error for actual fetch failures, not empty results
      set({ 
        restaurants: [], // Keep empty array for failed fetch
        error: 'Failed to load restaurants. Please try again.', 
        loading: false 
      });
    }
  },
  
  // Create new restaurant
  createRestaurant: async (userId: string, data: RestaurantFormDataWithLogo) => {
    set({ loading: true, error: null, uploadProgress: 0, isUploading: false });
    
    try {
      // Check if user already has a restaurant
      const hasReachedLimit = await get().checkRestaurantLimit(userId);
      if (hasReachedLimit) {
        throw new Error('You can only create one restaurant per account. Please refresh your page to see the existing one, and delete it to create a new one.');
      }
      
      const slug = generateSlug(data.name);
      let logoUrl: string | undefined;
      
      // First create the restaurant in Firestore to get the ID
      const restaurantData = {
        ownerId: userId,
        name: data.name,
        description: data.description || '',
        phone: data.phone || '',
        address: data.address || '',
        themeColor: data.themeColor,
        slug,
        isActive: true,
      };
      
      const restaurantId = await createRestaurantInFirestore(restaurantData);
      
      // Then upload the logo if provided
      if (data.logoFile) {
        const validation = validateImageFile(data.logoFile);
        if (!validation.isValid) {
          throw new Error(validation.error);
        }
        
        set({ isUploading: true });
        
        const uploadResult = await uploadRestaurantLogo(
          data.logoFile,
          restaurantId,
          (progress) => {
            set({ uploadProgress: progress.percentage });
          }
        );
        
        logoUrl = uploadResult.downloadURL;
        
        // Update the restaurant with the logo URL
        await updateRestaurantInFirestore(restaurantId, { logoUrl });
      }
      
      const newRestaurant: Restaurant = {
        id: restaurantId,
        ...restaurantData,
        logoUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      set((state) => ({
        restaurants: [newRestaurant, ...state.restaurants],
        loading: false,
        error: null,
        isUploading: false,
        uploadProgress: 0,
      }));
      
      return newRestaurant;
    } catch (error) {
      console.error('Error creating restaurant:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create restaurant. Please try again.', 
        loading: false,
        isUploading: false,
        uploadProgress: 0,
      });
      throw error;
    }
  },  
  // Update restaurant
  updateRestaurant: async (id: string, data: Partial<RestaurantFormDataWithLogo>) => {
    set({ loading: true, error: null, uploadProgress: 0, isUploading: false });
    
    try {
      let logoUrl: string | undefined;
      
      // Handle logo upload if a new file is provided
      if (data.logoFile) {
        const validation = validateImageFile(data.logoFile);
        if (!validation.isValid) {
          throw new Error(validation.error);
        }
        
        set({ isUploading: true });
        
        const uploadResult = await uploadRestaurantLogo(
          data.logoFile,
          id,
          (progress) => {
            set({ uploadProgress: progress.percentage });
          }
        );
        
        logoUrl = uploadResult.downloadURL;
      }
      
      const updateData: Partial<Omit<Restaurant, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>> = {
        name: data.name,
        description: data.description,
        phone: data.phone,
        address: data.address,
        themeColor: data.themeColor,
        logoUrl: logoUrl || undefined,
        slug: data.name ? generateSlug(data.name) : undefined,
      };
      
      // Clean undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });
      
      await updateRestaurantInFirestore(id, updateData);
      
      set((state) => ({
        restaurants: state.restaurants.map(restaurant =>
          restaurant.id === id
            ? {
                ...restaurant,
                ...updateData,
                logoUrl: logoUrl || restaurant.logoUrl,
                updatedAt: new Date(),
              }
            : restaurant
        ),
        loading: false,
        isUploading: false,
        uploadProgress: 0,
      }));
    } catch (error) {
      console.error('Error updating restaurant:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update restaurant. Please try again.', 
        loading: false,
        isUploading: false,
        uploadProgress: 0,
      });
      throw error;
    }
  },  
  // Delete restaurant
  deleteRestaurant: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      // Find the restaurant to check if it has assets before deletion
      const { restaurants } = get();
      const restaurant = restaurants.find(r => r.id === id);
      
      // Delete the restaurant from Firestore first
      await deleteRestaurantInFirestore(id);
      
      // Clean up all associated data (menu items, images, etc.) if restaurant exists
      if (restaurant) {
        try {
          await deleteRestaurantData(id);
        } catch (cleanupError) {
          // Log cleanup error but don't fail the restaurant deletion
          console.warn('Failed to complete restaurant cleanup:', cleanupError);
        }
      }
      
      // Add a small delay to show the loading animation
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Update the local state
      set((state) => ({
        restaurants: state.restaurants.filter(restaurant => restaurant.id !== id),
        loading: false,
      }));
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      set({ 
        error: 'Failed to delete restaurant. Please try again.', 
        loading: false 
      });
      throw error;
    }
  },
  
  // Clear error
  clearError: () => {
    set({ error: null });
  },
  
  // Set loading state (for navigation purposes)
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  
  // Reset store (for sign out)
  reset: () => {
    set({
      restaurants: [],
      loading: false,
      error: null,
      uploadProgress: 0,
      isUploading: false,
    });
  },
}));

export default useRestaurantStore;